/**
 * Extracts the filename from a Content-Disposition header, supporting both RFC5987 and fallback formats.
 */
export const getFilenameFromContentDisposition = (
  contentDisposition?: string | null,
): string | null => {
  if (!contentDisposition) return null;
  const filenameStarMatch = contentDisposition.match(/filename\*=(?:UTF-8''|\"?)([^\";]+)\"?/i);
  if (filenameStarMatch?.[1]) return decodeURIComponent(filenameStarMatch[1]);
  const filenameMatch = contentDisposition.match(/filename=\"?([^\";]+)\"?/i);
  return filenameMatch?.[1] ?? null;
};

/** Reads a response header case-insensitively from an axios-like headers object. */
export const getResponseHeader = (
  headers: Record<string, unknown> | undefined | null,
  name: string,
): string | null => {
  if (!headers) return null;
  const lower = name.toLowerCase();
  for (const [key, value] of Object.entries(headers)) {
    if (key.toLowerCase() === lower && value != null) {
      return String(value);
    }
  }
  return null;
};

/**
 * Filename for a public download: Content-Disposition (`filename*` then `filename`), else fallback.
 */
export const getPublicDownloadFilename = (
  headers: Record<string, unknown> | undefined | null,
  fallbackName: string,
): string => {
  const contentDisposition = getResponseHeader(headers, 'content-disposition');
  return getFilenameFromContentDisposition(contentDisposition) || fallbackName || 'download';
};

/**
 * Triggers a download in the browser for a given Blob and optional filename.
 */
export const triggerBlobDownload = (blob: Blob, filename?: string): void => {
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  if (filename) anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  // ponytail: delayed revoke — immediate revoke can break save-as in some browsers (e.g. Brave)
  window.setTimeout(() => window.URL.revokeObjectURL(url), 10_000);
};

export type NormalizedDownloadResult =
  | { type: 'blob'; blob: Blob; headers?: Record<string, unknown> }
  | { type: 'process'; processId: string };

/**
 * Normalizes the result of a download operation into a standard format.
 */
export const normalizeDownloadResult = (response: unknown): NormalizedDownloadResult | null => {
  if (!response) return null;

  const responseRecord = response as Record<string, unknown>;

  if (responseRecord.kind === 'blob' && responseRecord.data instanceof Blob) {
    return {
      type: 'blob',
      blob: responseRecord.data,
      headers: responseRecord.headers as Record<string, unknown> | undefined,
    };
  }

  if (responseRecord.data instanceof Blob) {
    return {
      type: 'blob',
      blob: responseRecord.data,
      headers: responseRecord.headers as Record<string, unknown> | undefined,
    };
  }

  if (response instanceof Blob) {
    return { type: 'blob', blob: response };
  }

  const nestedData = responseRecord.data as Record<string, unknown> | undefined;
  const processId =
    responseRecord.processId ||
    responseRecord.process_id ||
    nestedData?.processId ||
    nestedData?.process_id ||
    (typeof response === 'string' ? response : undefined);

  if (typeof processId === 'string' && processId.trim()) {
    return { type: 'process', processId };
  }

  return null;
};

const UUID_PATH_SEGMENT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Extracts a display/download filename from a zip/download URL.
 * Async multi-file zips are served at `/transfer/zip/{processId}`; that UUID is not
 * the archive name — BE sends `Content-Disposition: filename=files.zip` on GET, but
 * CORS blocks reading that header from XHR, so treat UUID path segments as `files.zip`.
 */
export const getFileNameFromUrl = (url: string): string => {
  try {
    const path = new URL(url).pathname;
    const segments = path.split('/').filter(Boolean);
    const last = decodeURIComponent(segments[segments.length - 1] ?? '');
    if (!last || UUID_PATH_SEGMENT.test(last) || !last.includes('.')) {
      return 'files.zip';
    }
    return last;
  } catch {
    return 'files.zip';
  }
};
